import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { AG_FILTER_TYPES_UNARY } from '@icp/components-core';
import { useEventCallback } from '@icp/hooks';
import Icon from '../../Icon';
import Popover from '../../Popover';
import FilterItemCondition from './FilterItemCondition';
import FilterItemValue from './FilterItemValue';
import useFilterItemDisplayText from './useFilterItemDisplayText';
import { filterHasValue, removeInvalidFilterValue } from './utils';

function FilterItem(props) {
  const {
    context,
    defaultOpen = false,
    columnDefs,
    model: modelProp,
    deletable = true,
    onChange,
    onDelete,
  } = props;

  const [model, setModel] = useState(modelProp);
  const [open, setOpen] = useState(false);
  const anchorEl = useRef(null);

  useEffect(() => {
    // effect 过后 anchorEl.current 才有值
    if (defaultOpen) {
      setOpen(true);
    }
    // 不响应 defaultOpen 的变化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setModel(modelProp);
  }, [modelProp]);

  const colDef = columnDefs.find((col) => col.colId === model.colId);

  // set filter 的 values 可能是异步的，hooks 一下
  const displayText = useFilterItemDisplayText(context, model, colDef);

  const handleChange = useEventCallback((k, v) => {
    let newModel = { ...model, [k]: v };
    if (k === 'type') {
      newModel = removeInvalidFilterValue(newModel);
    }
    setModel(newModel);
    if (filterHasValue(newModel)) {
      onChange(newModel);
    } else if (!open) {
      // for case：由于 debounce 导致的 handleChange 比 handleClose 触发的完的情况，
      // 如果这时候 filterHasValue 是 true，则需要清空一下
      newModel = { colId: model.colId, filterType: model.filterType, type: model.type };
      setModel(newModel);
      onChange(newModel);
    }
  });

  const handleClose = () => {
    setOpen(false);
    if (!filterHasValue(model)) {
      const newModel = { colId: model.colId, filterType: model.filterType, type: model.type };
      setModel(newModel);
      onChange(newModel);
    }
  };

  if (!colDef) {
    return null;
  }

  return (
    <>
      <button
        className={clsx('icp-table-filter-item icp-clickable icp-center', {
          'has-value': filterHasValue(model),
        })}
        ref={anchorEl}
        onClick={() => setOpen(true)}
        data-id={colDef.colId}
      >
        {displayText}
        <Icon name="oct:chevron-down" size={12} />
      </button>
      <Popover
        className="icp-table-filter-item-detail"
        open={open}
        anchorEl={anchorEl.current}
        onClose={handleClose}
      >
        <FilterItemCondition
          model={model}
          context={context}
          deletable={deletable}
          onChange={(v) => handleChange('type', v)}
          onDelete={onDelete}
        />
        {!AG_FILTER_TYPES_UNARY.includes(model.type) ? (
          <FilterItemValue
            key={model.type}
            context={context}
            colDef={colDef}
            model={model}
            onChange={handleChange}
          />
        ) : null}
      </Popover>
    </>
  );
}

FilterItem.propTypes = {
  context: PropTypes.shape({}),
  defaultOpen: PropTypes.bool,
  columnDefs: PropTypes.arrayOf(PropTypes.shape({})),
  model: PropTypes.shape({
    colId: PropTypes.string,
    filterType: PropTypes.string,
    type: PropTypes.string,
  }),
  deletable: PropTypes.bool,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
};

export default FilterItem;
